import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client, { getAccessToken, onAuthChange, setAccessToken } from "./client";
import type { User } from "./types";

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data } = await client.post("/auth/login", credentials);
      return data;
    },
    onSuccess: (data) => {
      setAccessToken(data.access);
      localStorage.setItem("refresh_token", data.refresh);
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const refresh = localStorage.getItem("refresh_token");
      await client.post("/auth/logout", { refresh }).catch(() => {});
    },
    onSettled: () => {
      localStorage.removeItem("refresh_token");
      queryClient.clear();
      setAccessToken(null); // fires onAuthChange last, so hasToken reads false
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: { old_password: string; new_password: string }) => {
      const { data } = await client.post("/auth/change-password", payload);
      return data;
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (payload: { email: string }) => {
      const { data } = await client.post("/auth/password-reset", payload);
      return data;
    },
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (payload: { uid: string; token: string; new_password: string }) => {
      const { data } = await client.post("/auth/password-reset/confirm", payload);
      return data;
    },
  });
}

export function useMe() {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(
    () => !!getAccessToken() || !!localStorage.getItem("refresh_token")
  );

  useEffect(() => {
    return onAuthChange(() => {
      const newHasToken = !!getAccessToken() || !!localStorage.getItem("refresh_token");
      setHasToken(newHasToken);
      // After a token refresh, the query may be in error state from the
      // initial 401. Invalidate so React Query refetches with the new token.
      if (newHasToken) {
        queryClient.invalidateQueries({ queryKey: ["me"] });
      }
    });
  }, [queryClient]);

  return useQuery<User>({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await client.get("/auth/me");
      return data.data;
    },
    retry: false,
    enabled: hasToken,
  });
}
